<template>
  <div>
    <button
      v-if="!href"
      :disabled="disabled"
      :aria-disabled="disabled || showLoading"
      :aria-busy="showLoading"
      :aria-label="ariaLabel"
      :class="classes"
      :type="buttonType"
      @click="!showLoading ? onDoClick() : undefined"
    >
      <div
        v-if="label"
        class="absolute top-90 left-[24rem] bg-white-50 px-4 py-1 text-14-18"
      >
        {{ label }}
      </div>
      <template v-if="showLoading">
        <div class="w-full">
          <div class="w-full flex justify-center">
            <nuxt-icon
              class="text-grey-500 [&>*]:animate-spin-slow-ease [&>*]:motion-reduce:animate-none"
              name="refresh"
              alt="laddningssymbol"
            />
          </div>
          <div class="invisible h-0">
            <slot></slot>
          </div>
        </div>
      </template>

      <template v-if="!showLoading">
        <slot></slot>
      </template>
    </button>

    <NuxtLink
      v-if="href"
      :to="href"
      class="focus-rounded"
      :class="classes"
      :aria-label="ariaLabel"
    >
      <slot></slot>
    </NuxtLink>
  </div>
</template>

<script lang="ts" setup>
export type ButtonType = 'button' | 'submit' | 'reset';
export type ButtonSize = 'default' | 'sm' | 'responsive';

interface Props {
  href?: string;
  color?: 'primary' | 'secondary';
  size?: ButtonSize;
  disabled?: boolean;
  ariaLabel?: string;
  buttonType?: ButtonType;
  showLoading?: boolean;
  fullWidth?: boolean;
  squared?: boolean;
  gtagEventName?: string;
  label?: string;
}

const props = withDefaults(defineProps<Props>(), {
  href: undefined,
  color: 'primary',
  size: 'default',
  disabled: false,
  ariaLabel: undefined,
  buttonType: 'submit',
  showLoading: false,
  fullWidth: false,
  squared: false,
  gtagEventName: '',
  label: '',
});

const emit = defineEmits(['onClick']);

const onDoClick = () => {
  emit('onClick');
};

const paddingClass = computed(() => {
  if (props.size === 'sm') {
    return 'p-8';
  }

  return 'p-16';
});

const heightClass = computed(() => {
  if (props.size === 'sm') {
    return 'h-44';
  }

  if (props.size === 'responsive') {
    return 'h-44 md:h-56';
  }

  return 'h-56';
});

const fontSizeClass = computed(() => {
  if (props.size === 'sm') {
    return 'text-18';
  }

  return 'text-24';
});

const widthClass = computed(() => {
  if (props.fullWidth) {
    return 'w-full';
  }

  if (props.squared) {
    if (props.size === 'sm') {
      return 'w-44';
    }
    if (props.size === 'responsive') {
      return 'w-44 md:w-56';
    }
    return 'w-56';
  }

  return 'w-fit';
});

const classes = computed(
  () => `
  ${paddingClass.value}
  ${heightClass.value}
  gap-16
  cursor-pointer
  flex
  justify-center
  items-center
  hover:bg-surface-action-hover
  active:bg-border-secondary
  border-2
  border-solid
  border-grey-500
  text-grey-500
  rounded
  shadow-button
  ${fontSizeClass.value}
  leading-[2.64rem]
  disabled:bg-white-500
  disabled:text-grey-200
  disabled:border-grey-200
  disabled:shadow-none
  no-underline
  ${widthClass.value}
  ${props.color !== 'secondary' ? 'bg-yellow-500' : 'bg-white-50'}
`,
);
</script>

<style scoped lang="scss"></style>
